import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  Pressable,
  TextInput,
  Platform,
  RefreshControl,
  ActivityIndicator,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { MaterialIcons, Ionicons } from '@expo/vector-icons';
import type { RootStackParamList } from '../navigation/stackTypes';
import { isSuccessful } from '../services/api-requests';
import Header from '../components/Header';
import LoadingButton from '../components/LoadingButton';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { BottomSheetModal, BottomSheetScrollView, BottomSheetView } from '../components/BottomSheet';
import type { BottomSheetModalRef } from '../components/BottomSheet';
import { getPricingGroups, createPricingGroup } from '../services/pricing-group-service';
import type {
  PricingGroup,
  CreatePricingGroupPayload,
  DiscountType,
  GroupScope,
} from '../models/pricing-group-model';
import { formatDiscountLabel, DISCOUNT_BADGE_COLORS } from '../models/pricing-group-model';
import { showThemedAlert } from '../components/common/ThemedAlert';

type NavigationProp = NativeStackNavigationProp<RootStackParamList, 'PricingGroupsManagement'>;

const DISCOUNT_TYPE_OPTIONS: { key: DiscountType; label: string }[] = [
  { key: 'free', label: 'Free' },
  { key: 'percentage', label: 'Percentage' },
  { key: 'flat_rate', label: 'Flat Rate' },
];

const SCOPE_OPTIONS: { key: GroupScope; label: string }[] = [
  { key: 'all_host_chargers', label: 'All My Chargers' },
  { key: 'specific_chargers', label: 'Select Chargers' },
];

export default function PricingGroupsManagement() {
  const navigation = useNavigation<NavigationProp>();
  const myChargers = useSelector((state: RootState) => state.chargers.myChargers);

  const [groups, setGroups] = useState<PricingGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  // Form state
  const [formName, setFormName] = useState('');
  const [formDiscountType, setFormDiscountType] = useState<DiscountType>('free');
  const [formDiscountValue, setFormDiscountValue] = useState('');
  const [formScope, setFormScope] = useState<GroupScope>('all_host_chargers');
  const [selectedChargerIds, setSelectedChargerIds] = useState<string[]>([]);
  const [creating, setCreating] = useState(false);

  const fetchGroups = useCallback(async () => {
    try {
      const response = await getPricingGroups();
      if (isSuccessful(response.status) && response.payload) {
        setGroups(response.payload.groups ?? []);
      } else {
        showThemedAlert('Error', 'Unable to load pricing groups. Please try again.');
      }
    } catch {
      showThemedAlert('Error', 'Something went wrong while loading pricing groups.');
    }
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await fetchGroups();
      setLoading(false);
    })();
  }, [fetchGroups]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await fetchGroups();
    setRefreshing(false);
  }, [fetchGroups]);

  const resetForm = useCallback(() => {
    setFormName('');
    setFormDiscountType('free');
    setFormDiscountValue('');
    setFormScope('all_host_chargers');
    setSelectedChargerIds([]);
  }, []);

  const bottomSheetRef = useRef<BottomSheetModalRef>(null);
  const snapPoints = useMemo(() => ['70%', '95%'], []);

  const openModal = useCallback(() => {
    resetForm();
    bottomSheetRef.current?.present();
  }, [resetForm]);

  const toggleCharger = useCallback((chargerId: string) => {
    setSelectedChargerIds(prev =>
      prev.includes(chargerId)
        ? prev.filter(id => id !== chargerId)
        : [...prev, chargerId],
    );
  }, []);

  const validateForm = useCallback((): boolean => {
    const trimmedName = formName.trim();
    if (!trimmedName) {
      showThemedAlert('Validation', 'Please enter a group name.');
      return false;
    }
    if (trimmedName.length < 2) {
      showThemedAlert('Validation', 'Group name must be at least 2 characters.');
      return false;
    }
    if (formDiscountType === 'percentage') {
      const val = parseInt(formDiscountValue, 10);
      if (isNaN(val) || val <= 0 || val > 100) {
        showThemedAlert('Validation', 'Percentage must be a whole number between 1 and 100.');
        return false;
      }
    }
    if (formDiscountType === 'flat_rate') {
      const val = parseFloat(formDiscountValue);
      if (isNaN(val) || val <= 0) {
        showThemedAlert('Validation', 'Please enter a valid rate greater than 0.');
        return false;
      }
    }
    if (formScope === 'specific_chargers' && selectedChargerIds.length === 0) {
      showThemedAlert('Validation', 'Please select at least one charger.');
      return false;
    }
    return true;
  }, [formName, formDiscountType, formDiscountValue, formScope, selectedChargerIds]);

  const handleCreate = useCallback(async () => {
    if (!validateForm()) return;

    setCreating(true);
    try {
      let discountValue = 0;
      if (formDiscountType === 'percentage') {
        discountValue = parseInt(formDiscountValue, 10);
      } else if (formDiscountType === 'flat_rate') {
        discountValue = parseFloat(formDiscountValue);
      }

      const payload: CreatePricingGroupPayload = {
        name: formName.trim(),
        discount_type: formDiscountType,
        discount_value: discountValue,
        scope: formScope,
        ...(formScope === 'specific_chargers' ? { charger_ids: selectedChargerIds } : {}),
      };

      const response = await createPricingGroup(payload);
      if (isSuccessful(response.status)) {
        bottomSheetRef.current?.dismiss();
        showThemedAlert('Success', `"${payload.name}" has been created.`);
        await fetchGroups();
      } else {
        showThemedAlert('Error', 'Failed to create pricing group. Please try again.');
      }
    } catch {
      showThemedAlert('Error', 'Something went wrong. Please try again.');
    } finally {
      setCreating(false);
    }
  }, [
    validateForm,
    formName,
    formDiscountType,
    formDiscountValue,
    formScope,
    selectedChargerIds,
    fetchGroups,
  ]);

  const getScopeLabel = (scope: GroupScope): string => {
    return scope === 'all_host_chargers' ? 'All Chargers' : 'Select Chargers';
  };

  const renderGroupCard = (group: PricingGroup) => {
    const badgeColor = DISCOUNT_BADGE_COLORS[group.discount_type];
    return (
      <Pressable
        key={group.id}
        style={({ pressed }) => [styles.groupCard, { opacity: pressed ? 0.85 : 1 }]}
        onPress={() => navigation.navigate('PricingGroupDetail', { groupId: group.id })}
      >
        <View style={styles.cardTopRow}>
          <Text style={styles.groupName} numberOfLines={1}>
            {group.name}
          </Text>
          <View style={[styles.discountBadge, { backgroundColor: badgeColor }]}>
            <Text style={styles.discountBadgeText}>
              {formatDiscountLabel(group.discount_type, group.discount_value)}
            </Text>
          </View>
        </View>

        <View style={styles.cardBottomRow}>
          <View style={styles.metaItem}>
            <Ionicons name="people-outline" size={14} color="#8D8E90" />
            <Text style={styles.metaText}>
              {group.member_count} {group.member_count === 1 ? 'member' : 'members'}
            </Text>
          </View>

          <View style={styles.metaDivider} />

          <View style={styles.metaItem}>
            <MaterialIcons name="ev-station" size={14} color="#8D8E90" />
            <Text style={styles.metaText}>
              {group.charger_count} {group.charger_count === 1 ? 'charger' : 'chargers'}
            </Text>
          </View>

          <View style={styles.metaDivider} />

          <View style={styles.metaItem}>
            <MaterialIcons name="language" size={14} color="#8D8E90" />
            <Text style={styles.metaText}>{getScopeLabel(group.scope)}</Text>
          </View>

          <View style={{ flex: 1 }} />

          <View style={styles.statusContainer}>
            <View
              style={[
                styles.statusDot,
                { backgroundColor: group.is_active ? '#10B981' : '#6B7280' },
              ]}
            />
            <Text
              style={[
                styles.statusText,
                { color: group.is_active ? '#10B981' : '#6B7280' },
              ]}
            >
              {group.is_active ? 'Active' : 'Inactive'}
            </Text>
          </View>
        </View>
      </Pressable>
    );
  };

  const renderEmptyState = () => (
    <View style={styles.emptyContainer}>
      <MaterialIcons name="group-add" size={72} color="#3A3E44" />
      <Text style={styles.emptyTitle}>No Pricing Groups</Text>
      <Text style={styles.emptySubtitle}>
        Create a pricing group to offer discounts{'\n'}to specific users on your chargers.
      </Text>
      <Pressable
        style={({ pressed }) => [styles.emptyCreateBtn, { opacity: pressed ? 0.85 : 1 }]}
        onPress={openModal}
      >
        <MaterialIcons name="add" size={20} color="#FFFFFF" />
        <Text style={styles.emptyCreateBtnText}>Create Group</Text>
      </Pressable>
    </View>
  );

  const renderDiscountValueInput = () => {
    if (formDiscountType === 'free') return null;

    const isPercent = formDiscountType === 'percentage';
    const placeholder = isPercent ? 'e.g. 25' : 'e.g. 0.150';
    const suffix = isPercent ? '%' : '$';

    const handleValueChange = (text: string) => {
      if (isPercent) {
        const cleaned = text.replace(/[^0-9]/g, '');
        const num = parseInt(cleaned, 10);
        if (cleaned === '' || (num >= 0 && num <= 100)) {
          setFormDiscountValue(cleaned);
        }
      } else {
        setFormDiscountValue(text);
      }
    };

    return (
      <View style={styles.formSection}>
        <Text style={styles.formLabel}>Discount Value</Text>
        <View style={styles.valueInputRow}>
          <TextInput
            style={[styles.textInput, { flex: 1 }]}
            placeholder={placeholder}
            placeholderTextColor="#555"
            keyboardType={isPercent ? 'number-pad' : 'decimal-pad'}
            value={formDiscountValue}
            onChangeText={handleValueChange}
            returnKeyType="done"
            maxLength={isPercent ? 3 : undefined}
          />
          <View style={styles.valueSuffix}>
            <Text style={styles.valueSuffixText}>{suffix}</Text>
          </View>
        </View>
      </View>
    );
  };

  const renderChargerChecklist = () => {
    if (formScope !== 'specific_chargers') return null;

    return (
      <View style={styles.formSection}>
        <Text style={styles.formLabel}>Select Chargers</Text>
        {myChargers.length === 0 ? (
          <Text style={styles.noChargersText}>No chargers found.</Text>
        ) : (
          <ScrollView
            style={styles.chargerListScroll}
            nestedScrollEnabled
            showsVerticalScrollIndicator
          >
            {myChargers.map(charger => {
              const chargerId = charger.uuid ?? '';
              const isSelected = selectedChargerIds.includes(chargerId);
              return (
                <Pressable
                  key={chargerId}
                  style={({ pressed }) => [
                    styles.chargerCheckItem,
                    isSelected && styles.chargerCheckItemSelected,
                    { opacity: pressed ? 0.8 : 1 },
                  ]}
                  onPress={() => toggleCharger(chargerId)}
                >
                  <View
                    style={[
                      styles.checkbox,
                      isSelected && styles.checkboxSelected,
                    ]}
                  >
                    {isSelected && (
                      <Ionicons name="checkmark" size={14} color="#FFFFFF" />
                    )}
                  </View>
                  <View style={styles.chargerCheckInfo}>
                    <Text style={styles.chargerCheckName} numberOfLines={1}>
                      {charger.name || 'Unnamed Charger'}
                    </Text>
                    <Text style={styles.chargerCheckSerial} numberOfLines={1}>
                      {charger.unit_serial}
                    </Text>
                  </View>
                </Pressable>
              );
            })}
          </ScrollView>
        )}
      </View>
    );
  };

  const renderCreateSheet = () => (
    <BottomSheetModal
      ref={bottomSheetRef}
      index={0}
      snapPoints={snapPoints}
      enablePanDownToClose
      backgroundStyle={styles.sheetBackground}
      handleIndicatorStyle={styles.sheetHandle}
      style={Platform.OS === 'web' ? styles.sheetWeb : undefined}
    >
      <View style={styles.modalHeaderRow}>
        <Text style={styles.modalTitle}>Create Pricing Group</Text>
        <Pressable
          onPress={() => bottomSheetRef.current?.dismiss()}
          hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
        >
          <MaterialIcons name="close" size={24} color="#8D8E90" />
        </Pressable>
      </View>

      <BottomSheetScrollView
        contentContainerStyle={styles.sheetScrollContent}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
      >
        {/* Name */}
        <View style={styles.formSection}>
          <Text style={styles.formLabel}>Group Name</Text>
          <TextInput
            style={styles.textInput}
            placeholder="e.g. Family & Friends"
            placeholderTextColor="#555"
            value={formName}
            onChangeText={setFormName}
            autoCapitalize="words"
            returnKeyType="next"
            maxLength={60}
          />
        </View>

        {/* Discount Type */}
        <View style={styles.formSection}>
          <Text style={styles.formLabel}>Discount Type</Text>
          <View style={styles.chipRow}>
            {DISCOUNT_TYPE_OPTIONS.map(option => {
              const active = formDiscountType === option.key;
              return (
                <Pressable
                  key={option.key}
                  style={[styles.chip, active && styles.chipActive]}
                  onPress={() => {
                    setFormDiscountType(option.key);
                    if (option.key === 'free') setFormDiscountValue('');
                  }}
                >
                  <Text
                    style={[styles.chipText, active && styles.chipTextActive]}
                  >
                    {option.label}
                  </Text>
                </Pressable>
              );
            })}
          </View>
        </View>

        {/* Discount Value */}
        {renderDiscountValueInput()}

        {/* Scope */}
        <View style={styles.formSection}>
          <Text style={styles.formLabel}>Applies To</Text>
          <View style={styles.chipRow}>
            {SCOPE_OPTIONS.map(option => {
              const active = formScope === option.key;
              return (
                <Pressable
                  key={option.key}
                  style={[styles.chip, active && styles.chipActive]}
                  onPress={() => {
                    setFormScope(option.key);
                    if (option.key === 'all_host_chargers') setSelectedChargerIds([]);
                  }}
                >
                  <Text
                    style={[styles.chipText, active && styles.chipTextActive]}
                  >
                    {option.label}
                  </Text>
                </Pressable>
              );
            })}
          </View>
        </View>

        {/* Charger Checklist */}
        {renderChargerChecklist()}

        {/* Create Button */}
        <View style={styles.modalFooter}>
          <LoadingButton
            onPress={handleCreate}
            disabled={creating}
            isLoading={creating}
            backgroundColor="#39BEB7"
            style={styles.createButton}
            hapticsOnStart
            hapticsOnSuccess
          >
            Create Group
          </LoadingButton>
        </View>
      </BottomSheetScrollView>
    </BottomSheetModal>
  );

  if (loading) {
    return (
      <SafeAreaView edges={['top']} style={styles.safeArea}>
        <Header headerText="Pricing Groups" />
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#40BFB4" />
          <Text style={styles.loadingText}>Loading groups...</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView edges={['top']} style={styles.safeArea}>
      <Header headerText="Pricing Groups" />

      {/* Create button row */}
      <View style={styles.topBar}>
        <Text style={styles.groupCount}>
          {groups.length} {groups.length === 1 ? 'group' : 'groups'}
        </Text>
        <Pressable
          style={({ pressed }) => [styles.addButton, { opacity: pressed ? 0.85 : 1 }]}
          onPress={openModal}
        >
          <MaterialIcons name="add" size={18} color="#FFFFFF" />
          <Text style={styles.addButtonText}>Create</Text>
        </Pressable>
      </View>

      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={
          groups.length === 0 ? styles.emptyScrollContent : styles.scrollContent
        }
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={handleRefresh}
            tintColor="#40BFB4"
            colors={['#40BFB4']}
          />
        }
        showsVerticalScrollIndicator={false}
      >
        {groups.length === 0 ? renderEmptyState() : groups.map(renderGroupCard)}
      </ScrollView>

      {renderCreateSheet()}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#1A1D21',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 12,
  },
  loadingText: {
    color: '#8D8E90',
    fontSize: 14,
    fontFamily: 'PlusJakartaSans-Regular',
  },
  topBar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingBottom: 12,
  },
  groupCount: {
    color: '#8D8E90',
    fontSize: 14,
    fontFamily: 'PlusJakartaSans-Regular',
  },
  addButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#39BEB7',
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 20,
    gap: 4,
  },
  addButtonText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '600',
    fontFamily: 'PlusJakartaSans-Regular',
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: 16,
    paddingBottom: 32,
  },
  emptyScrollContent: {
    flexGrow: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 16,
  },

  // Group Card
  groupCard: {
    backgroundColor: '#292D33',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    ...Platform.select({
      ios: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.15,
        shadowRadius: 4,
      },
      android: {
        elevation: 3,
      },
    }),
  },
  cardTopRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 12,
    gap: 10,
  },
  groupName: {
    color: '#FFFFFF',
    fontSize: 17,
    fontWeight: '600',
    fontFamily: 'PlusJakartaSans-Regular',
    flexShrink: 1,
  },
  discountBadge: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
  },
  discountBadgeText: {
    color: '#FFFFFF',
    fontSize: 12,
    fontWeight: '700',
    fontFamily: 'PlusJakartaSans-Regular',
  },
  cardBottomRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 6,
  },
  metaItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  metaText: {
    color: '#8D8E90',
    fontSize: 12,
    fontFamily: 'PlusJakartaSans-Regular',
  },
  metaDivider: {
    width: 1,
    height: 12,
    backgroundColor: '#3A3E44',
    marginHorizontal: 2,
  },
  statusContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
  },
  statusDot: {
    width: 7,
    height: 7,
    borderRadius: 3.5,
  },
  statusText: {
    fontSize: 12,
    fontWeight: '600',
    fontFamily: 'PlusJakartaSans-Regular',
  },

  // Empty State
  emptyContainer: {
    alignItems: 'center',
    gap: 12,
    paddingBottom: 40,
  },
  emptyTitle: {
    color: '#FFFFFF',
    fontSize: 20,
    fontWeight: '600',
    fontFamily: 'PlusJakartaSans-Regular',
    marginTop: 8,
  },
  emptySubtitle: {
    color: '#8D8E90',
    fontSize: 14,
    fontFamily: 'PlusJakartaSans-Regular',
    textAlign: 'center',
    lineHeight: 20,
  },
  emptyCreateBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#39BEB7',
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 24,
    gap: 6,
    marginTop: 8,
  },
  emptyCreateBtnText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
    fontFamily: 'PlusJakartaSans-Regular',
  },

  // Modal
  sheetBackground: {
    backgroundColor: '#1A1D21',
    borderRadius: 20,
  },
  sheetHandle: {
    backgroundColor: '#3A3E44',
    width: 40,
    height: 4,
  },
  sheetWeb: {
    maxWidth: 430,
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  sheetScrollContent: {
    paddingHorizontal: 20,
    paddingBottom: Platform.OS === 'ios' ? 34 : 16,
  },
  modalHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingTop: 8,
    paddingBottom: 16,
  },
  modalTitle: {
    color: '#FFFFFF',
    fontSize: 20,
    fontWeight: '700',
    fontFamily: 'PlusJakartaSans-Regular',
  },
  modalFooter: {
    paddingHorizontal: 20,
    paddingTop: 12,
  },
  createButton: {
    borderRadius: 24,
  },

  // Form
  formSection: {
    marginBottom: 20,
  },
  formLabel: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '600',
    fontFamily: 'PlusJakartaSans-Regular',
    marginBottom: 8,
  },
  textInput: {
    backgroundColor: '#292D33',
    borderRadius: 10,
    padding: 14,
    color: '#FFFFFF',
    fontSize: 15,
    fontFamily: 'PlusJakartaSans-Regular',
    borderWidth: 1,
    borderColor: '#3A3E44',
  },
  valueInputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  valueSuffix: {
    backgroundColor: '#292D33',
    borderRadius: 10,
    paddingHorizontal: 14,
    paddingVertical: 14,
    borderWidth: 1,
    borderColor: '#3A3E44',
  },
  valueSuffixText: {
    color: '#8D8E90',
    fontSize: 14,
    fontWeight: '600',
    fontFamily: 'PlusJakartaSans-Regular',
  },
  chipRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  chip: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 20,
    backgroundColor: '#292D33',
    borderWidth: 1,
    borderColor: '#3A3E44',
  },
  chipActive: {
    backgroundColor: 'rgba(64,191,180,0.15)',
    borderColor: '#40BFB4',
  },
  chipText: {
    color: '#8D8E90',
    fontSize: 14,
    fontFamily: 'PlusJakartaSans-Regular',
    fontWeight: '500',
  },
  chipTextActive: {
    color: '#40BFB4',
    fontWeight: '600',
  },

  // Charger Checklist
  chargerListScroll: {
    maxHeight: 200,
    borderRadius: 10,
    backgroundColor: '#22262B',
    borderWidth: 1,
    borderColor: '#3A3E44',
  },
  chargerCheckItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 14,
    paddingVertical: 12,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: '#3A3E44',
    gap: 12,
  },
  chargerCheckItemSelected: {
    backgroundColor: 'rgba(64,191,180,0.08)',
  },
  checkbox: {
    width: 22,
    height: 22,
    borderRadius: 6,
    borderWidth: 1.5,
    borderColor: '#555',
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkboxSelected: {
    backgroundColor: '#39BEB7',
    borderColor: '#39BEB7',
  },
  chargerCheckInfo: {
    flex: 1,
  },
  chargerCheckName: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '500',
    fontFamily: 'PlusJakartaSans-Regular',
  },
  chargerCheckSerial: {
    color: '#8D8E90',
    fontSize: 12,
    fontFamily: 'PlusJakartaSans-Regular',
    marginTop: 2,
  },
  noChargersText: {
    color: '#8D8E90',
    fontSize: 13,
    fontFamily: 'PlusJakartaSans-Regular',
    fontStyle: 'italic',
    paddingVertical: 8,
  },
});
